import { useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Textarea } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import {
  useAssignHomework,
  useCreateHomework,
  useCurriculum,
  useHomeworkTemplates,
  useStudents,
} from '../../hooks/queries';

export interface SetHomeworkDialogProps {
  open: boolean;
  onClose: () => void;
}

function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Creates a piece of homework and hands it out in one pass.
 *
 * The two are separate on the API — a definition can be reused across students and
 * terms — but a tutor almost always wants both at once, so the form does the pair and
 * offers reuse of an existing definition rather than forcing a two-step flow.
 */
export function SetHomeworkDialog({ open, onClose }: SetHomeworkDialogProps) {
  const { data: students } = useStudents({ page: 1 });
  const { data: curriculum } = useCurriculum();
  const { data: templates } = useHomeworkTemplates(1);
  const createHomework = useCreateHomework();
  const assignHomework = useAssignHomework();

  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [existingId, setExistingId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const topics = useMemo(() => {
    const subject = curriculum?.find((s) => s.id === subjectId);
    return subject?.courses.flatMap((course) => course.topics) ?? [];
  }, [curriculum, subjectId]);

  const roster = students?.items ?? [];
  const isPending = createHomework.isPending || assignHomework.isPending;

  const toggleStudent = (id: string): void => {
    setStudentIds((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    );
  };

  const close = (): void => {
    setError(null);
    setNotice(null);
    onClose();
  };

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (studentIds.length === 0) {
      setError('Select at least one student.');
      return;
    }
    if (mode === 'existing' && !existingId) {
      setError('Choose a piece of homework to assign.');
      return;
    }

    try {
      const homeworkId =
        mode === 'existing'
          ? existingId
          : (
              await createHomework.mutateAsync({
                title: title.trim(),
                description: description.trim() || undefined,
                instructions: instructions.trim() || undefined,
                subjectId: subjectId || undefined,
                topicId: topicId || undefined,
                maxScore: maxScore ? Number(maxScore) : undefined,
                estimatedMinutes: estimatedMinutes ? Number(estimatedMinutes) : undefined,
              })
            ).id;

      // Due at end of day, which is what "due Friday" means to a student.
      const dueAt = new Date(`${dueDate}T23:59:00`).toISOString();

      const result = await assignHomework.mutateAsync({ homeworkId, studentIds, dueAt });

      if (result.assigned === 0 && result.skipped > 0) {
        setNotice('Those students already have this homework.');
        return;
      }

      close();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fieldError('dueAt') ?? err.fieldError('title') ?? err.message);
        return;
      }
      setError('We could not set that homework.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Set homework"
      description="Create a new piece of work, or hand out one you have already written."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" form="set-homework-form" isLoading={isPending}>
            Set homework
          </Button>
        </>
      }
    >
      <form id="set-homework-form" onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}
        {notice && <Alert tone="info">{notice}</Alert>}

        {roster.length === 0 && (
          <Alert tone="info" title="No students yet">
            Invite a student from the Students page before setting homework.
          </Alert>
        )}

        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {(
            [
              { value: 'new', label: 'Write new' },
              { value: 'existing', label: 'Reuse existing' },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMode(option.value)}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                mode === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {mode === 'existing' ? (
          <Select
            label="Homework"
            value={existingId}
            onChange={(event) => setExistingId(event.target.value)}
          >
            <option value="">Choose…</option>
            {templates?.items.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
                {template.subject ? ` · ${template.subject.name}` : ''}
              </option>
            ))}
          </Select>
        ) : (
          <>
            <Input
              label="Title"
              required
              placeholder="Integration by Parts — Practice"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />

            <Textarea
              label="Description"
              hint="Optional. A one-line summary."
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />

            <Textarea
              label="Instructions"
              hint="Optional. What the student should actually do."
              rows={4}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Subject"
                value={subjectId}
                onChange={(event) => {
                  setSubjectId(event.target.value);
                  setTopicId('');
                }}
              >
                <option value="">No subject</option>
                {curriculum?.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Topic"
                disabled={!subjectId}
                value={topicId}
                onChange={(event) => setTopicId(event.target.value)}
              >
                <option value="">No topic</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Marks available"
                type="number"
                min={1}
                max={1000}
                placeholder="30"
                value={maxScore}
                onChange={(event) => setMaxScore(event.target.value)}
              />
              <Input
                label="Estimated minutes"
                type="number"
                min={1}
                max={600}
                placeholder="40"
                value={estimatedMinutes}
                onChange={(event) => setEstimatedMinutes(event.target.value)}
              />
            </div>
          </>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Assign to</p>
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {roster.length === 0 ? (
              <p className="px-2 py-3 text-sm text-slate-500">Your roster is empty.</p>
            ) : (
              roster.map((student) => (
                <label
                  key={student.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={studentIds.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-900">
                    {student.user.fullName}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        <Input
          label="Due date"
          type="date"
          required
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          hint="Due at the end of the chosen day."
        />
      </form>
    </Modal>
  );
}
